import React from 'react';

import { AlertMessageDTO } from 'root/apps/components/AlertMessageDisplay/AlertMessageDisplay';
import useLocalizedLink from 'root/apps/hooks/useLocalizedLink';
import { ToasterAlertMessage } from 'root/apps/NetworkHealthTool/components/ToasterAlertMessage';
import {
  ClassificationNames,
  NamedEvent,
  NetworkTestEntity,
} from 'root/apps/NetworkHealthTool/services/NetworkHealthToolService';
import { useNetworkTestService } from 'root/apps/NetworkHealthTool/services/useNetworkHealthToolService';
import { NetworkEventType } from 'root/apps/NetworkHealthTool/utils/channelUtils';
import { logError } from 'root/datadog/datadogLogs';
import Modules from 'shared_modules/Modules';

const isTestBad = (test: NetworkTestEntity | null): boolean => {
  if (!test || !test.scores || !test.scores.rtc) return false;
  return (
    test.scores.rtc.classificationName === ClassificationNames.BAD ||
    test.scores.rtc.classificationName === ClassificationNames.POOR
  );
};

const isTestGood = (test: NetworkTestEntity | null): boolean => {
  if (!test || !test.scores || !test.scores.rtc) return false;
  return (
    test.scores.rtc.classificationName === ClassificationNames.AVERAGE ||
    test.scores.rtc.classificationName === ClassificationNames.GOOD ||
    test.scores.rtc.classificationName === ClassificationNames.GREAT
  );
};

const areTestsInScope = (
  testA: NetworkTestEntity | null,
  testB: NetworkTestEntity | null
): boolean => {
  if (!testA || !testB) return false;
  const timeDiff = Math.abs(testA.timestamp - testB.timestamp);
  return timeDiff <= 60 * 60 * 1000; // 60 minutes
};

const LearnMoreLink: React.FC = () => {
  const SUPPORT_ARTICLE_URL = useLocalizedLink(
    'https://retail-support.lightspeedhq.com/hc/{{locale}}/articles/1260800114289-Networking-best-practices'
  );
  return (
    <a href={SUPPORT_ARTICLE_URL} target="_blank" rel="noreferrer" data-test="LearnMoreLink">
      Learn more
    </a>
  );
};

export const NetworkMonitor: React.FC = () => {
  const service = useNetworkTestService();
  const [alertMessage, setAlertMessage] = React.useState<AlertMessageDTO | null>(null);
  const [isOnline, setIsOnline] = React.useState<boolean>(window.navigator.onLine);

  const triggerDegradedNetwork = React.useCallback(
    async (test: NetworkTestEntity) => {
      try {
        await service.updateNetworkTest(test.id, {
          namedEvent: NamedEvent.NETWORK_DEGRADATION_STARTED,
        });
      } catch (error) {
        logError(error, {
          message: 'Failed to update network test with degradation event',
          source: 'NetworkMonitor',
          file: 'NetworkMonitor.tsx',
        });
      }
      if (!isOnline) return;
      setAlertMessage({
        type: 'warning',
        message: (
          <span>
            Network connection unstable. <LearnMoreLink />
          </span>
        ),
      });
    },
    [service, isOnline, setAlertMessage]
  );

  const triggerNetworkRestored = React.useCallback(
    async (test?: NetworkTestEntity) => {
      try {
        if (test) {
          await service.updateNetworkTest(test.id, { namedEvent: NamedEvent.NETWORK_RECOVERED });
        }
      } catch (error) {
        logError(error, {
          message: 'Failed to update network test with recovery event',
          source: 'NetworkMonitor',
          file: 'NetworkMonitor.tsx',
        });
      }
      if (!isOnline) return;
      setAlertMessage({
        type: 'success',
        message: (
          <span>
            Network connection restored. <LearnMoreLink />
          </span>
        ),
      });
    },
    [isOnline, service]
  );

  const triggerNetworkLost = React.useCallback(() => {
    setAlertMessage({
      type: 'danger',
      message: 'Lost network connection',
    });
  }, [setAlertMessage]);

  /**
   * New Test event handlers
   * ==========================================================================
   */
  const handleNetworkTestAdded = React.useCallback(async () => {
    try {
      const result = await service.getLastNetworkTests(3);
      if (!result || result.length < 2) return;
      const testA = result[0];
      const testB = result[1];
      const testC = result[2];
      if (!areTestsInScope(testA, testB)) return;
      if (isTestBad(testA) && isTestBad(testB)) {
        if (!testC || (areTestsInScope(testB, testC) && isTestGood(testC))) {
          triggerDegradedNetwork(testA);
          return;
        }
      }
      if (isTestGood(testA) && isTestBad(testB)) {
        if (testC && areTestsInScope(testB, testC) && isTestBad(testC)) {
          triggerNetworkRestored(testA);
        }
      }
    } catch (error) {
      logError(error, {
        message: 'Failed to handle new network test',
        source: 'NetworkMonitor',
        file: 'NetworkMonitor.tsx',
      });
    }
  }, [triggerDegradedNetwork, triggerNetworkRestored, service]);

  React.useEffect(() => {
    if (service) {
      service.on(NetworkEventType.NETWORK_TEST_ADDED, handleNetworkTestAdded);
      return () => {
        service.off(NetworkEventType.NETWORK_TEST_ADDED, handleNetworkTestAdded);
      };
    }
    return undefined;
  }, [handleNetworkTestAdded, service]);

  /**
   * Online/Offline event handlers
   * ==========================================================================
   */
  const handleOnlineOffline = React.useCallback(() => {
    setIsOnline(window.navigator.onLine);
  }, [setIsOnline]);

  React.useEffect(() => {
    window.addEventListener('offline', handleOnlineOffline);
    window.addEventListener('online', handleOnlineOffline);
    return () => {
      window.removeEventListener('offline', handleOnlineOffline);
      window.removeEventListener('online', handleOnlineOffline);
    };
  }, [handleOnlineOffline]);

  React.useEffect(() => {
    if (!isOnline && (!alertMessage || alertMessage.type !== 'danger')) {
      triggerNetworkLost();
    } else if (isOnline && alertMessage && alertMessage.type === 'danger') {
      triggerNetworkRestored();
    }
  }, [alertMessage, triggerNetworkLost, triggerNetworkRestored, isOnline]);

  if (!Modules.hasModule('support.network_health_tool.display_ui')) return null;
  if (!alertMessage) return null;

  return <ToasterAlertMessage type={alertMessage.type} message={alertMessage.message} />;
};
